import { ErrorOutline, InfoOutlined } from '@mui/icons-material'
import { Components, Theme } from '@mui/material'

declare module '@mui/material/Alert' {
  interface AlertPropsColorOverrides {
    neutral: true
    'error-strong': true
    // TODO - uncomment when error severity removed
    // error: false
  }

  interface AlertPropsVariantOverrides {
    muted: true
    filled: false
    // TODO - set muted to false when migration complete
  }
}

// Text buttons in the action get custom styles per severity
const actionButtonSelector = '& .MuiAlert-action .MuiButton-root.MuiButton-text.MuiButton-colorSecondary'

const CONTENT_HEIGHT = '28px'

export const MuiAlert: Components<Omit<Theme, 'components'>>['MuiAlert'] = {
  defaultProps: {
    // The custom severities have no MUI default icon; the built-in severities
    // still fall through to MUI's defaultIconMapping per-severity
    iconMapping: {
      // TODO - proper icons
      neutral: <InfoOutlined fontSize="inherit" />,
      'error-strong': <ErrorOutline fontSize="inherit" />,
    },
    slotProps: {
      // Most actions are centred, but override for the close button
      closeButton: {
        sx: {
          alignSelf: 'flex-start',
        },
      },
    },
  },
  styleOverrides: {
    icon: ({ theme }) => ({
      marginRight: theme.spacing(2.5),
      padding: 0,
      paddingTop: theme.spacing(0.5),
      minHeight: CONTENT_HEIGHT,
    }),
    action: ({ theme }) => ({
      marginRight: 0,
      // Ensure close action is top aligned without spacing
      paddingTop: theme.spacing(0),
      // Most actions are centred, close button is overridden above
      alignItems: 'center',
    }),
    message: ({ theme }) => ({
      padding: 0,
      alignSelf: 'center',
      '&:has(.MuiAlertTitle-root)': {
        ...theme.typography['small-regular'],
      },
      '& .MuiAlertTitle-root': {
        ...theme.typography['body-medium'],
      },
    }),
    root: ({ theme }) => ({
      padding: theme.spacing(2, 2, 2, 4),
      '&:not(:has(.MuiAlert-action))': {
        paddingRight: theme.spacing(4),
      },
      borderRadius: theme.shape['radius-lg'],
      borderStyle: 'solid',
      borderWidth: theme.strokeWidth.default,
      ...theme.typography['body-medium'],

      '& pre': {
        padding: theme.spacing(2.5, 4),
        backgroundColor: theme.palette.bg.surface.inset,
        borderRadius: theme.shape['radius-md'],
        margin: 0,
        marginTop: theme.spacing(1),
        whiteSpace: 'pre-wrap',
      },
    }),
  },
  variants: [
    {
      props: { severity: 'neutral' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.surface.elevated,
        color: theme.palette.text.default,
        borderColor: theme.palette.border.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.fg.default,
        },
        // neutral severity button just uses standard secondary text
      }),
    },
    {
      props: { severity: 'info' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.info.muted,
        color: theme.palette.text.info.onInfoMuted,
        borderColor: theme.palette.border.info.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.fg.info.onInfoMuted,
        },
        [actionButtonSelector]: {
          color: theme.palette.text.info.onInfoMuted,
        },
      }),
    },
    {
      props: { severity: 'success' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.success.muted,
        color: theme.palette.text.success.onSuccessMuted,
        borderColor: theme.palette.border.success.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.fg.success.onSuccessMuted,
        },
        [actionButtonSelector]: {
          color: theme.palette.text.success.onSuccessMuted,
        },
      }),
    },
    {
      props: { severity: 'warning' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.warning.muted,
        color: theme.palette.text.warning.onWarningMuted,
        borderColor: theme.palette.border.warning.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.fg.warning.onWarningMuted,
        },
        [actionButtonSelector]: {
          color: theme.palette.text.warning.onWarningMuted,
        },
      }),
    },
    {
      props: { severity: 'error' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.danger.muted,
        color: theme.palette.text.danger.onDangerMuted,
        borderColor: theme.palette.border.danger.subtle,
        '& .MuiAlert-icon': {
          color: theme.palette.fg.danger.onDangerMuted,
        },
        [actionButtonSelector]: {
          color: theme.palette.text.danger.onDangerMuted,
        },
      }),
    },
    {
      props: { severity: 'error-strong' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.bg.danger.default,
        color: theme.palette.text.danger.onDanger,
        borderColor: theme.palette.bg.danger.default,
        '& .MuiAlert-icon': {
          color: theme.palette.fg.danger.onDanger,
        },
        [actionButtonSelector]: {
          color: theme.palette.text.danger.onDanger,
        },
      }),
    },
  ],
}
